'use client';
import * as React from 'react';
import { Controller, Control, UseFormWatch } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select-2024';
import { AspectRatioSelector } from '@components/Pack/SizeSelectors';
import { api } from '@/trpc/react';
import { match } from 'ts-pattern';
import { useSession } from '@hooks/index';
import {
  convertToReadableText,
  formatText,
  DEFAULT_PHOTO_ASPECT_RATIO,
  DEFAULT_INITIAL_GENERATION_WORKFLOW_PIPELINE,
  PIPELINES,
  ALLOWED_PIPELINES
} from '@acme/lib';
import { getModelVersionFromWorkflowName } from '@acme/lib';
import { DEFAULT_PHOTO_ALBUM_PHOTO_COUNT } from '@lib/global';

import { UseTRPCQueryResult } from '@trpc/react-query/shared';
import '@acme/db/types';
import { GLASSES_VALUES } from '@data/constants';
import { isOptionalWildcard } from '@acme/lib';
import { Skeleton } from '@components/ui/skeleton';
import { OUTFIT_MODIFIER_RULES } from '@acme/lib';
// import { ModelSelector } from '@components/ModelSelector/ModelSelector';
import { ModelTrainingState } from '@/atoms/useTrainingAtom';
import { CartState } from '@/atoms/useCartAtom';
import usePhotoPackAction from '@/atoms/usePhotoPackAction';
import { CategoryButton } from './CategoryButton';
import { ModelSelector } from '@components/ModelSelector';

function AddToCartFormContent({
  className,
  control,
  watch,
  themeId,
  modelVersion,
  currentAmountOfPacksAllowed,
  currentQuantity,
  onGoNext,
  gender
}: {
  className?: string;
  control: Control<any>;
  watch: UseFormWatch<any>;
  gender?: 'male' | 'female';
  themeId: string;
  modelVersion?: string;
  currentAmountOfPacksAllowed?: number;
  currentQuantity?: number;
  onGoNext: (data: { wildcardCategory?: boolean; wildcardModifierKey?: string; category: string }) => void;
  modelSelectorMode?: 'training' | 'selection';
}) {
  const themeWithWildcardsQuery = api.theme.getThemeWithWildcards.useQuery(
    { themeId: themeId!, gender: gender ? [gender] : undefined },
    { enabled: !!themeId }
  );

  const { session } = useSession();

  const watchSize = watch('photoSize.size');
  const watchWorkflow = watch('workflow');

  const modelVersionFromWorkflowName = getModelVersionFromWorkflowName(watchWorkflow || '');
  const maxQuantity =
    currentAmountOfPacksAllowed && currentQuantity ? Math.max(currentAmountOfPacksAllowed, currentQuantity) : 4;
  const quantityOptions = Array.from({ length: maxQuantity }, (_, i) => i + 1);

  // Helper function to render selected value components - keep this outside CategoryButton
  const renderSelectedValues = (fieldValue: any, category: string) => {
    if (!fieldValue) return [];

    return Object.entries(fieldValue || {})
      .filter(([key]) => key === category)
      .map(([_categoryKey, categoryValue]) => {
        if (typeof categoryValue !== 'object') return null;
        return Object.entries(categoryValue as Record<string, Record<string, string>>).map(([key, value]) => {
          const readableKey = convertToReadableText(key);
          return (
            <div key={key} className="flex flex-col items-end gap-1 min-w-0 w-full">
              <div className="text-muted-foreground text-[10px] leading-none w-full text-right">{readableKey}</div>
              <div className="text-blue-400 text-sm leading-none text-right truncate break-all text-wrap line-clamp-1">
                {String(value.value || '').replace(/\{.*?\}/g, '')}
              </div>
            </div>
          );
        });
      })
      .filter(Boolean)
      .flat();
  };

  return (
    <div className={cn('grid items-start', className)}>
      <div className="flex gap-4 bg-red-500/0 overflow-hidden">
        <div className="flex-1 flex flex-col gap-3">
          <Label htmlFor="size">Aspect Ratio</Label>
          <Controller
            name="photoSize.aspectRatio"
            control={control}
            render={({ field }) => (
              <AspectRatioSelector
                selectedAspectRatio={field.value || DEFAULT_PHOTO_ASPECT_RATIO}
                onAspectRatioChange={field.onChange}
                modelVersion={(modelVersion as '1.5' | 'flux.1-dev') || modelVersionFromWorkflowName}
                selectedSize={watchSize || 'small'}
                variant="radio"
              />
            )}
          />
        </div>
      </div>

      {/* START WILDARDS */}
      <div className="flex flex-col gap-2 bg-red-500/0 py-6">
        {match(themeWithWildcardsQuery)
          .with({ status: 'success' }, ({ data }) => {
            const hasOutfitWildcard = data.wildcards.some((wildcard: string) => wildcard.includes('triggerOutfit'));

            return hasOutfitWildcard ? (
              <div className="mb-2">
                <Label htmlFor="clothing" className="mb-2">
                  Clothing
                </Label>
                <div className="flex flex-col gap-2">
                  {Object.entries(OUTFIT_MODIFIER_RULES).map(([key, config]) => (
                    <Controller
                      key={key}
                      name="selectedModifiers"
                      control={control}
                      render={({ field }) => {
                        const selectedValues = renderSelectedValues(field.value, key);

                        return (
                          <CategoryButton
                            category={key}
                            selectedValues={selectedValues}
                            isOptional={!config.required}
                            onClick={() => onGoNext({ wildcardCategory: true, category: key })}
                          />
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : null;
          })
          .otherwise(() => null)}
        {/* START REQUIRED WILDARDS */}
        <Label htmlFor="quantity">Customize</Label>
        {/* START CLOTHING SECTION */}

        {match(themeWithWildcardsQuery)
          .with({ status: 'pending' }, () => (
            <div className="flex flex-col gap-3">
              <Skeleton className="w-2/3 h-4" />
            </div>
          ))
          .with({ status: 'success' }, ({ data }) => {
            const modifierCategories = Object.keys(data.modifiers);
            const requiredCategories = modifierCategories.filter(category => !isOptionalWildcard(category));

            return (
              <>
                {requiredCategories.map(category => {
                  const categoryModifiers = data.modifiers[category];
                  const hasMultipleCategories = categoryModifiers && Object.keys(categoryModifiers).length > 1;
                  const singleCategory = categoryModifiers && Object.keys(categoryModifiers)[0];

                  return (
                    <Controller
                      key={category}
                      name="selectedModifiers"
                      control={control}
                      render={({ field }) => {
                        const selectedValues = renderSelectedValues(field.value, category);

                        return (
                          <CategoryButton
                            category={category}
                            selectedValues={selectedValues}
                            onClick={() =>
                              onGoNext(
                                hasMultipleCategories
                                  ? { wildcardCategory: true, category }
                                  : { wildcardModifierKey: singleCategory, category }
                              )
                            }
                          />
                        );
                      }}
                    />
                  );
                })}
              </>
            );
          })
          .with({ status: 'error' }, () => <div>Error loading modifiers</div>)
          .exhaustive()}

        {/* START OPTIONAL WILDARDS */}
        {match(themeWithWildcardsQuery)
          .with({ status: 'pending' }, () => (
            <div className="flex flex-col gap-3">
              <Skeleton className="w-2/3 h-4" />
            </div>
          ))
          .with({ status: 'success' }, ({ data }) => {
            const modifierCategories = Object.keys(data.modifiers);
            const optionalCategories = modifierCategories.filter(isOptionalWildcard);

            return optionalCategories.length > 0 ? (
              <>
                <Label htmlFor="quantity" className="mt-4">
                  Optional
                </Label>
                <div className="flex flex-col gap-2">
                  {optionalCategories.map(category => {
                    const categoryModifiers = data.modifiers[category];
                    const hasMultipleCategories = categoryModifiers && Object.keys(categoryModifiers).length > 1;
                    const singleCategory = categoryModifiers && Object.keys(categoryModifiers)[0];

                    return (
                      <Controller
                        key={category}
                        name="selectedModifiers"
                        control={control}
                        render={({ field }) => {
                          const selectedValues = renderSelectedValues(field.value, category);

                          return (
                            <CategoryButton
                              category={category}
                              selectedValues={selectedValues}
                              isOptional={true}
                              onClick={() =>
                                onGoNext(
                                  hasMultipleCategories
                                    ? { wildcardCategory: true, category }
                                    : { wildcardModifierKey: singleCategory, category }
                                )
                              }
                            />
                          );
                        }}
                      />
                    );
                  })}
                </div>
              </>
            ) : null;
          })
          .with({ status: 'error' }, () => <div>Error loading modifiers</div>)
          .exhaustive()}

        {/* START GLASSES (ADMIN AND OPTIONAL) */}
        {session?.user?.isAdmin && (
          <Controller
            name="selectedModifiers.glasses"
            control={control}
            render={({ field }) => (
              <CategoryButton
                category="glasses"
                selectedValues={
                  field.value
                    ? [
                        <div key="glasses" className="text-sm text-blue-400 text-right">
                          {field.value === GLASSES_VALUES.MODEL_GLASSES ? 'My glasses' : field.value}
                        </div>
                      ]
                    : []
                }
                onClick={() => onGoNext({ wildcardCategory: true, category: 'glasses' })}
              />
            )}
          />
        )}
        {session?.user?.isAdmin && (
          <Controller
            name="garmentImages"
            control={control}
            render={({ field }) => (
              <CategoryButton
                category="garment"
                categoryName="Custom Clothing"
                selectedValues={
                  Array.isArray(field.value) && field.value.length > 0
                    ? [
                        <div key="custom-clothing" className="text-sm text-blue-400 text-right">
                          Custom Clothing
                        </div>
                      ]
                    : []
                }
                isOptional={true}
                onClick={() => onGoNext({ wildcardCategory: true, category: 'garment' })}
              />
            )}
          />
        )}
      </div>

      {/* PHOTO PACK QUANTITY */}
      <div className="hidden gap-3">
        <Label htmlFor="quantity">Quantity</Label>
        <Controller
          name="quantity"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value?.toString() || '1'}>
              <SelectTrigger>
                <SelectValue placeholder="1" />
              </SelectTrigger>
              <SelectContent>
                {quantityOptions.map(quantity => (
                  <SelectItem key={quantity} value={quantity.toString()} className="">
                    <span>
                      {quantity} {quantity > 1 ? 'packs' : 'pack'}
                    </span>{' '}
                    <span className="text-muted-foreground text-xs ml-1">
                      ({quantity * DEFAULT_PHOTO_ALBUM_PHOTO_COUNT} Photos)
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* PHOTO QUANTITY */}
      {session?.user?.isAdmin && (
        <div className="grid gap-3">
          <Label htmlFor="photoQuantity">Photo Quantity</Label>
          <Controller
            name="photoQuantity"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value?.toString() || '1'}>
                <SelectTrigger>
                  <SelectValue placeholder="1" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 5, 10, 15, 20, 40].map(quantity => (
                    <SelectItem key={quantity} value={quantity.toString()} className="">
                      <span>
                        {quantity} {quantity > 1 ? 'photos' : 'photo'}
                      </span>{' '}
                      <span className="text-muted-foreground text-xs ml-1">({quantity} Photos)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {/* WORKFLOW SELECTOR */}
      {session?.user?.isAdmin && (
        <div className="grid gap-3 mt-4">
          <Label htmlFor="workflowName">Workflow Pipeline</Label>
          <Controller
            name="workflowName"
            control={control}
            render={({ field }: { field: { onChange: (value: string) => void; value: string } }) => {
              const formatPipelineServices = (pipelineName: string) => {
                const pipeline = PIPELINES[pipelineName as keyof typeof PIPELINES];
                if (!pipeline) return '';

                const services = Object.values(pipeline).map(step => {
                  const serviceName = step.service.split('/').pop() || '';
                  // First remove -production suffix, then replace remaining dashes with spaces
                  return serviceName.replace(/-production$/, '').replace(/-/g, ' ');
                });

                return services.join(' → ');
              };

              return (
                <Select
                  onValueChange={field.onChange}
                  value={field.value || DEFAULT_INITIAL_GENERATION_WORKFLOW_PIPELINE}
                >
                  <SelectTrigger>
                    <SelectValue>
                      {formatText(field.value || DEFAULT_INITIAL_GENERATION_WORKFLOW_PIPELINE, 'title')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ALLOWED_PIPELINES.map(pipelineName => (
                      <SelectItem key={pipelineName} value={pipelineName}>
                        <div className="flex flex-col gap-1">
                          <span>{formatText(pipelineName, 'title')}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatText(formatPipelineServices(pipelineName), 'title')}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />
        </div>
      )}
    </div>
  );
}

export function PhotoPackOptionsForm({
  Title,
  themeQuery,
  control,
  watch,
  gender,
  themeId,
  modelVersion,
  currentAmountOfPacksAllowed,
  currentQuantity,
  onSubmit,
  onGoNext,
  modelSelectorMode,
  selectedModel,
  onModelChange,
  modelLocked
}: {
  Title: string;
  themeQuery: UseTRPCQueryResult<any, any>;
  control: any;
  watch: any;
  gender?: 'male' | 'female';
  themeId: string;
  modelVersion?: string;
  currentAmountOfPacksAllowed?: number;
  currentQuantity: number;
  onSubmit: () => void;
  onGoNext: (data: { wildcardCategory?: boolean; wildcardModifierKey?: string; category: string }) => void;
  modelSelectorMode?: 'training' | 'selection';
  selectedModel?: ModelTrainingState | CartState['model'] | null;
  onModelChange?: (model: CartState['model']) => void;
  modelLocked?: boolean;
}) {
  const { themes } = usePhotoPackAction({
    mode: modelSelectorMode === 'training' ? 'training' : 'cart'
  });
  return match(themeQuery)
    .with({ status: 'pending' }, () => (
      <div className="flex flex-col gap-3 items-center justify-center h-full">
        <div className="w-full max-w-4xl mx-auto p-6 space-y-6">
          <Skeleton className="aspect-[4/5] w-full max-w-24 mx-auto" />
          <Skeleton className="h-6 w-1/2 mx-auto" />

          <div className="space-y-4">
            <Skeleton className="h-6 w-32" />
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-32 w-full" />
                </div>
              ))}
            </div>
          </div>

          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    ))
    .with({ status: 'success' }, ({ data }) => (
      <div className="flex flex-col gap-4 h-full px-1">
        <div className="flex flex-col justify-center items-center gap-4 px-1">
          <img
            src={`https://www.picstudio.ai/api/og/photo-pack/${data?.theme.slug}`}
            alt={data?.theme.name || Title}
            className="object-cover rounded-lg max-h-24 w-auto"
          />
          <h2 className="text-3xl font-bold mb-6 sr-only">{data?.theme.name || Title}</h2>
        </div>
        <form id="updatePhotoPackForm" onSubmit={onSubmit} className="space-y-4">
          <ModelSelector
            isLocked={modelSelectorMode === 'training' || modelLocked}
            onModelChange={data =>
              onModelChange?.({
                ...data,
                readableName: data.modelRepositoryName!,
                status: data.status!,
                teamId: data.teamId!,
                avatarUrl: data.avatarUrl!,
                modelRepositoryId: data.modelRepositoryId!,
                version: data.version!,
                name: data.name!,
                id: data.id!
              })
            }
            showConfirmation={themes?.length > 0}
            selectedModel={{
              id: typeof selectedModel === 'object' && 'id' in selectedModel! ? selectedModel.id : undefined,
              name: selectedModel?.name!,
              avatarUrl:
                typeof selectedModel === 'object' && 'avatarUrl' in selectedModel!
                  ? selectedModel.avatarUrl
                  : selectedModel?.selfieUrl,
              version:
                typeof selectedModel === 'object' && 'version' in selectedModel! ? selectedModel.version : 'flux.1-dev',
              modelRepositoryId:
                typeof selectedModel === 'object' && 'modelRepositoryId' in selectedModel!
                  ? selectedModel.modelRepositoryId!
                  : undefined,
              modelRepositoryName:
                typeof selectedModel === 'object' && 'modelRepositoryName' in selectedModel!
                  ? (selectedModel.modelRepositoryName as string)
                  : selectedModel?.name!,
              status:
                typeof selectedModel === 'object' && 'status' in selectedModel! ? selectedModel.status! : 'active',
              teamId:
                typeof selectedModel === 'object' && 'teamId' in selectedModel! ? selectedModel.teamId! : undefined
            }}
          />
          <AddToCartFormContent
            modelSelectorMode={modelSelectorMode}
            modelVersion={modelVersion}
            control={control}
            watch={watch}
            gender={gender}
            themeId={themeId}
            currentAmountOfPacksAllowed={currentAmountOfPacksAllowed}
            currentQuantity={currentQuantity || 0}
            onGoNext={onGoNext}
          />
        </form>
      </div>
    ))
    .with({ status: 'error' }, () => <div>Error loading theme</div>)
    .exhaustive();
}
